import type { Appetite, Diet, Expertise } from "@/types/profile";
import type { IconName } from "@/components/onboarding-icons/OnboardingIcons";

/** Curated list of common allergens shown as swipeable cards (BRD §3.1). */
export const ALLERGY_DECK: readonly {
  id: string;
  label: string;
  icon: IconName;
}[] = [
  { id: "gluten", label: "Gluten", icon: "gluten" },
  { id: "lactose", label: "Lactose", icon: "lactose" },
  { id: "egg", label: "Œufs", icon: "egg" },
  { id: "peanut", label: "Arachides", icon: "peanut" },
  { id: "tree_nuts", label: "Fruits à coque", icon: "tree_nuts" },
  { id: "soy", label: "Soja", icon: "soy" },
  { id: "fish", label: "Poisson", icon: "fish" },
  { id: "shellfish", label: "Crustacés", icon: "shellfish" },
  { id: "sesame", label: "Sésame", icon: "sesame" },
  { id: "mustard", label: "Moutarde", icon: "mustard" },
  { id: "celery", label: "Céleri", icon: "celery" },
  { id: "sulphites", label: "Sulfites", icon: "sulphites" },
];

export const EQUIPMENT_OPTIONS: readonly {
  id: string;
  label: string;
  icon: IconName;
}[] = [
  { id: "stovetop", label: "Plaques de cuisson", icon: "stovetop" },
  { id: "oven", label: "Four", icon: "oven" },
  { id: "microwave", label: "Micro-ondes", icon: "microwave" },
  { id: "blender", label: "Mixeur / Blender", icon: "blender" },
  {
    id: "food_processor",
    label: "Robot multifonction",
    icon: "food_processor",
  },
  { id: "instant_pot", label: "Cocotte-minute", icon: "instant_pot" },
  { id: "slow_cooker", label: "Mijoteuse", icon: "slow_cooker" },
  { id: "air_fryer", label: "Friteuse à air", icon: "air_fryer" },
];

export const DIETS: readonly {
  id: Diet;
  label: string;
  icon: IconName;
  hint: string;
}[] = [
  {
    id: "omnivore",
    label: "Omnivore",
    icon: "omnivore",
    hint: "Aucune restriction",
  },
  {
    id: "vegetarian",
    label: "Végétarien",
    icon: "vegetarian",
    hint: "Pas de viande ni de poisson",
  },
  {
    id: "vegan",
    label: "Végan",
    icon: "vegan",
    hint: "Aucun produit animal",
  },
  {
    id: "pescatarian",
    label: "Pescétarien",
    icon: "pescatarian",
    hint: "Poisson autorisé",
  },
];

export const EXPERTISE: readonly {
  id: Expertise;
  label: string;
  icon: IconName;
  hint: string;
}[] = [
  {
    id: "express",
    label: "Express",
    icon: "express",
    hint: "< 20 min par repas",
  },
  {
    id: "normal",
    label: "Normal",
    icon: "normal",
    hint: "Recettes équilibrées",
  },
  { id: "chef", label: "Chef", icon: "chef", hint: "Techniques avancées" },
];

export const APPETITE: readonly {
  id: Appetite;
  label: string;
  hint: string;
}[] = [
  { id: "light", label: "Légères", hint: "Portions légères · ~600 kcal/pers." },
  {
    id: "normal",
    label: "Équilibrées",
    hint: "Portions équilibrées · ~750 kcal/pers.",
  },
  {
    id: "generous",
    label: "Généreuses",
    hint: "Portions généreuses · ~900 kcal/pers.",
  },
];

export const ONBOARDING_STEPS = [
  "size",
  "diet",
  "meat-days",
  "allergies",
  "equipment",
  "expertise",
  "appetite",
  "batch-schedule",
  "food-exclusions",
  "review",
] as const;
export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number];

/** Weekday labels short (Mon-first, ISO order matching backend). */
export const WEEKDAY_LABELS_SHORT = [
  "Lun",
  "Mar",
  "Mer",
  "Jeu",
  "Ven",
  "Sam",
  "Dim",
] as const;

/** Weekday labels long (Mon-first). */
export const WEEKDAY_LABELS_LONG = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
] as const;
